import {
  Button,
  column,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  Tooltip,
  useModal,
} from "@homebound/beam";
import { PropsWithChildren, useMemo } from "react";
import { useParams } from "react-router";
import { Price } from "src/components";
import { PageHeader } from "src/routes/layout/PageHeader";
import { CheckoutPricingTraceModal } from "src/routes/libraries/product-offerings/checkout-pricing-trace/CheckoutPricingTraceModal";
import { fail, queryResult } from "src/utils";
import {
  CheckoutPricingTrace_OptionGroupFragment,
  CheckoutPricingTracePage_OptionStatsFragment,
  CheckoutPricingTracePageOverview_TraceFragment,
  useCheckoutPricingTracePage_OverviewQuery,
} from "./CheckoutPricingTracePage.generated";

/** Shows the per-option group included/price drift of a `CheckoutConfig`, linked by the trade costs that drove the diff. */
export function CheckoutPricingTracePage() {
  const { configId } = useParams<{ configId: string }>();
  const query = useCheckoutPricingTracePage_OverviewQuery({ variables: { ccfgId: configId } });
  return (
    <>
      <PageHeader title="Checkout Pricing Trace" />
      {queryResult(query, ({ checkoutPricingTrace }) => (
        <CheckoutPricingTracePageView trace={checkoutPricingTrace} />
      ))}
    </>
  );
}

type CheckoutPricingTracePageViewProps = {
  trace: CheckoutPricingTracePageOverview_TraceFragment;
};

function CheckoutPricingTracePageView({ trace }: CheckoutPricingTracePageViewProps) {
  const columns = useMemo(
    () => createColumns(trace.checkoutConfig?.productOffering?.id ?? fail("Expected a checkoutConfig.productOffering")),
    [trace],
  );
  const rows = useMemo(() => createRows(trace), [trace]);
  return <GridTable columns={columns} rows={rows} />;
}

type HeaderRow = { kind: "header"; data: CheckoutPricingTracePageOverview_TraceFragment };
type GroupRow = {
  kind: "group";
  id: string;
  data: CheckoutPricingTrace_OptionGroupFragment;
  // The last selected full config, if any
  lastSelected: CheckoutPricingTracePage_OptionStatsFragment | undefined;
  // The last included full config, if any
  lastIncluded: CheckoutPricingTracePage_OptionStatsFragment | undefined;
};
type Row = HeaderRow | GroupRow;

function createColumns(pofId: string): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Group",
      group: (g) => {
        const path = g.group.location?.displayLocationPath;
        return (
          <Tooltip title={g.group.id} closeDelay={3000}>
            <div css={Css.df.fdc.$}>
              <div css={Css.sm.$}>{g.group.name}</div>
              {path && <div>{path}</div>}
            </div>
          </Tooltip>
        );
      },
    }),
    column<Row>({
      header: (data) => (
        <div css={Css.df.fdc.$}>
          <div>Included Config</div>
          <Price valueInCents={data.includedPriceInCents} />
        </div>
      ),
      group: (g, { row }) => {
        return <GroupCell pofId={pofId} thisOption={g.included} lastOption={row.lastIncluded} />;
      },
    }),
    column<Row>({
      header: (data) => (
        <div css={Css.df.fdc.$}>
          <div>Selected Config</div>
          <Price valueInCents={data.selectedPriceInCents} />
        </div>
      ),
      group: (g, { row }) => {
        return <GroupCell pofId={pofId} thisOption={g.selected} lastOption={row.lastSelected} />;
      },
    }),
    numericColumn<Row>({
      header: "Credit",
      group: (g) => {
        // Selections have two credits, UW selection & package upgrade, so show them in a tooltip
        function MaybeTooltip({ children }: PropsWithChildren) {
          return g.includedSelectionPriceInCents ? (
            <Tooltip
              title={
                <div>
                  Included <Price valueInCents={g.includedSelectionPriceInCents} /> + Upgrade{" "}
                  <Price valueInCents={g.packageUpgradeCreditInCents} />
                </div>
              }
              closeDelay={3000}
            >
              {children}
            </Tooltip>
          ) : (
            <>{children}</>
          );
        }

        return (
          <MaybeTooltip>
            <Price valueInCents={g.availableCreditInCents} />
          </MaybeTooltip>
        );
      },
      w: "100px",
    }),
    column<Row>({
      header: "Price",
      group: (g, { row }) => {
        return g.included?.option === null ? (
          "N/A"
        ) : g.priceChangeInCents === 0 ? (
          "Included"
        ) : (
          <Price valueInCents={g.priceChangeInCents} displayDirection={true} />
        );
      },
    }),
  ];
}

function createRows(trace: CheckoutPricingTracePageOverview_TraceFragment): GridDataRow<Row>[] {
  let lastSelected: CheckoutPricingTracePage_OptionStatsFragment | undefined = undefined;
  let lastIncluded: CheckoutPricingTracePage_OptionStatsFragment | undefined = undefined;
  return [
    { id: "header", kind: "header" as const, data: trace },
    // We don't sort the option groups, b/c the drift calc is fundamentally sequential, i.e. each
    // RPOG price/drift depends on the RPOG before it.
    ...trace.optionGroups.map((og) => {
      const row = {
        id: og.group.id,
        kind: "group" as const,
        data: og,
        lastSelected,
        lastIncluded,
      };
      lastSelected = og.selected ? og.selected : lastSelected;
      lastIncluded = og.included ? og.included : lastIncluded;
      return row;
    }),
  ];
}

function GroupCell(props: {
  pofId: string;
  lastOption: CheckoutPricingTracePage_OptionStatsFragment | undefined;
  thisOption: CheckoutPricingTracePage_OptionStatsFragment | undefined | null;
}) {
  const { pofId, lastOption, thisOption } = props;
  const { openModal } = useModal();

  const diff = diffFullConfig(lastOption, thisOption);

  function changeLabel() {
    if (thisOption && !diff) {
      return "(included)";
    } else if (diff) {
      const { added, removed } = diff;
      // If this was the only add, show a simpler label
      if (removed.length === 0 && added.length === 1) {
        return "(added)";
      } else {
        return (
          <Tooltip
            title={
              <div>
                {added.map((name) => (
                  <div key={name}>+ {name}</div>
                ))}
                {removed.map((name) => (
                  <div key={name}>- {name}</div>
                ))}
              </div>
            }
          >
            (added...)
          </Tooltip>
        );
      }
    }
    return "";
  }

  return (
    <div css={Css.df.fdc.gap1.$}>
      {thisOption?.option?.name} {changeLabel()}
      <div css={Css.df.gap2.aic.$}>
        <Price valueInCents={thisOption?.totalPriceInCents} />
        <Price valueInCents={thisOption?.directCostInCents} />
        {diff && (
          <Button
            variant="text"
            label="diff"
            onClick={() => {
              openModal({
                content: (
                  <CheckoutPricingTraceModal
                    pofId={pofId}
                    oldConfig={lastOption!.fullConfig}
                    newConfig={thisOption!.fullConfig}
                  />
                ),
                size: { width: "xxl", height: 700 },
              });
            }}
          />
        )}
      </div>
    </div>
  );
}

/** Shows the config drift from group to group, initially as the full list, and then diffs. */
function diffFullConfig(
  lastOption: CheckoutPricingTracePage_OptionStatsFragment | undefined,
  thisOption: CheckoutPricingTracePage_OptionStatsFragment | undefined | null,
): { added: string[]; removed: string[] } | undefined {
  if (lastOption && thisOption) {
    const a = new Set(lastOption.fullConfig);
    const b = new Set(thisOption.fullConfig);
    const added = [...b.difference(a)];
    const removed = [...a.difference(b)];
    if (added.length > 0 || removed.length > 0) {
      return {
        added: added.map((o) => o.name),
        removed: removed.map((o) => o.name),
      };
    }
  }
  return undefined;
}
